import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

import { Grid, TextField, Paper, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { acCleanCompany, acGetAllApplicationServices } from '../../store/actions';
import { DataTable, PrimaryButton } from '../default';
import { CreateApplicationServiceModal } from '../modals';

/**
 * @description Renders a table component for displaying application services with search and filter capabilities.
 *
 * @author Maria Eduarda Franke
 * @returns {React.Component} The rendered table component.
 */
const ApplicationServicesTable = () => {
    const dispatch = useDispatch();

    const [openCreateModal, setOpenCreateModal] = useState(false);

    // Filtros
    const [search, setSearch] = useState('');
    const [date, setDate] = useState(null);

    // Get All Application Services
    const allApplicationServices = useSelector(state => state.applicationServices.allApplicationServices);
    const isLoadingAllApplicationServices = useSelector(state => state.applicationServices.isLoadingAllApplicationServices);
    const hasErrorAllApplicationServices = useSelector(state => state.applicationServices.hasErrorAllApplicationServices);
    // Create Application Service
    const isCreatingApplicationService = useSelector(state => state.applicationServices.isCreatingApplicationService);

    useEffect(() => {
        dispatch(acGetAllApplicationServices())
            .catch(error => console.error(error));
        // eslint-disable-next-line
    }, []);

    // Filtro aplicado nos dados
    const filteredApplicationServices = (allApplicationServices || []).filter(service => {
        const matchesSearch = !search ||
            service.name?.toLowerCase().includes(search.toLowerCase()) ||
            service.company?.name?.toLowerCase().includes(search.toLowerCase());
        const matchesDate = !date ||
            (service.createdAt &&
                dayjs(service.createdAt).format('YYYY-MM-DD') === dayjs(date).format('YYYY-MM-DD'));
        return matchesSearch && matchesDate;
    });

    const columns = [
        {
            accessorKey: 'company_application_service_id',
            header: 'ID',
            enableEditing: false,
            size: 80
        },
        {
            accessorKey: 'name',
            header: 'Nome'
        },
        {
            accessorKey: 'company.name',
            header: 'Razão Social'
        },
        {
            accessorKey: 'createdAt',
            header: 'Data de Criação',
            Cell: ({ cell }) => renderDateColumn(cell)
        }
    ];

    const renderDateColumn = (cell) => {
        if (!cell.getValue()) return '-';
        const date = dayjs(cell.getValue());
        return date.locale('pt-br').format('DD/MM/YYYY HH:mm');
    };

    // Handle Create Action
    const onCreate = () => {
        setOpenCreateModal(!openCreateModal);
    };

    // Handle Close Action
    const onClose = () => {
        setOpenCreateModal(!openCreateModal);
        dispatch(acCleanCompany());
    };

    return (
        <Paper elevation={1} sx={{ padding: 3 }}>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item flexGrow={1}>
                    <TextField
                        fullWidth
                        placeholder="Buscar aplicação"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        size="small"
                    />
                </Grid>
                <Grid item xs='auto'>
                    <DatePicker
                        label="Selecionar data"
                        value={date}
                        onChange={setDate}
                        format="DD/MM/YYYY"
                        slotProps={{
                            textField: { size: 'small', fullWidth: true },
                            actionBar: { actions: ['clear'] }
                        }}
                    />
                </Grid>
                <Grid item xs='auto'>
                    <PrimaryButton
                        label="Criar Aplicação"
                        Icon={AddIcon}
                        onClick={onCreate}
                    />
                </Grid>
            </Grid>

            <CreateApplicationServiceModal
                open={openCreateModal}
                onClose={() => onClose()}
            />

            <DataTable
                resource='Aplicação'
                gender='fem'
                tableType='createOnly'
                columns={columns}
                data={filteredApplicationServices}
                onCreate={onCreate}
                isLoading={isLoadingAllApplicationServices}
                isSaving={isCreatingApplicationService}
                showAlertBanner={hasErrorAllApplicationServices}
                showButton={false}
            />
        </Paper>
    );
};

export default ApplicationServicesTable;